import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Brand, ThemeToggle } from "@/components/common";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function TeamInvite() {
  const { code } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", captain_name: "", color: "#007AFF", logo_url: null });
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    api.get(`/team-invites/${code}`).then((r) => setInvite(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [code]);

  const create = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/team-invites/${code}/teams`, form);
      toast.success("Team created");
      nav(`/c/${data.captain_code}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Center>{error}</Center>;
  if (!invite) return <Center>Loading…</Center>;
  const t = invite.tournament;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b px-5 py-3">
        <Brand onClick={() => nav("/")} />
        <ThemeToggle />
      </header>
      <main className="mx-auto max-w-md px-4 py-8">
        <Card className="space-y-5 p-6">
          <div className="flex items-center gap-3">
            {t.logo_url && <img src={fileUrl(t.logo_url)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
            <div>
              <h1 className="font-heading text-2xl font-bold">Create your team</h1>
              <p className="text-sm text-muted-foreground">Join {t.name}{t.location ? ` · ${t.location}` : ""}</p>
            </div>
          </div>

          {!invite.available ? (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">This invitation has reached its team limit.</p>
          ) : (
            <>
              <div><Label>Team name *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="invite-team-name" /></div>
              <div><Label>Captain name</Label><Input value={form.captain_name} onChange={(e) => set("captain_name", e.target.value)} className="mt-1.5" data-testid="invite-captain-name" /></div>
              <div>
                <Label>Team color</Label>
                <div className="mt-1.5 flex gap-2"><input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="h-10 w-14 rounded-md border" /><Input value={form.color} onChange={(e) => set("color", e.target.value)} /></div>
              </div>
              <div><Label>Team logo</Label><div className="mt-1.5"><ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="invite-team-logo" /></div></div>
              <Button className="w-full" onClick={create} disabled={busy || !form.name.trim()} data-testid="create-invited-team">{busy ? "Creating…" : "Create team"}</Button>
              <p className="text-center text-xs text-muted-foreground">After creating the team, you will continue as its captain.</p>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}

const Center = ({ children }) => <div className="flex h-screen items-center justify-center px-4 text-center text-muted-foreground">{children}</div>;
